import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { AdminAuthProvider, useAdminAuth } from "@/context/AdminAuthContext";
import AppLayout from "@/components/layout/AppLayout";
import Login from "@/pages/Login";
import AcceptInvite from "@/pages/AcceptInvite";
import Dashboard from "@/pages/Dashboard";
import Analytics from "@/pages/Analytics";
import Campaigns from "@/pages/Campaigns";
import Leads from "@/pages/Leads";
import CallLogs from "@/pages/CallLogs";
import Inbound from "@/pages/Inbound";
import AIAgent from "@/pages/AIAgent";
import Integrations from "@/pages/Integrations";
import Billing from "@/pages/Billing";
import Support from "@/pages/Support";
import WhatsNew from "@/pages/WhatsNew";
import Workflows from "@/pages/Workflows";
import WorkflowBuilder from "@/pages/WorkflowBuilder";
import Settings from "@/pages/Settings";
import AdminLayout from "@/admin/AdminLayout";
import AdminLogin from "@/admin/AdminLogin";
import AdminOverview from "@/admin/AdminOverview";
import AdminClients from "@/admin/AdminClients";
import AdminClientDetail from "@/admin/AdminClientDetail";
import AdminSystem from "@/admin/AdminSystem";
import AdminSecurity from "@/admin/AdminSecurity";
import AdminBilling from "@/admin/AdminBilling";
import AdminAnalytics from "@/admin/AdminAnalytics";
import AdminTelephony from "@/admin/AdminTelephony";
import AdminAI from "@/admin/AdminAI";
import AdminAnnouncements from "@/admin/AdminAnnouncements";
import AdminTickets from "@/admin/AdminTickets";
import AdminSettings from "@/admin/AdminSettings";
import AdminUpdates from "@/admin/AdminUpdates";
import AdminPlaceholder from "@/admin/AdminPlaceholder";
import { Loader2 } from "lucide-react";

function Protected({ children }) {
  const { user } = useAuth();
  if (user === null) return <div className="h-screen flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-indigo-500" /></div>;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function AdminProtected({ children }) {
  const { admin } = useAdminAuth();
  if (admin === null) return <div className="h-screen flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin" style={{ color: "#7C3AED" }} /></div>;
  if (!admin) return <Navigate to="/admin/login" replace />;
  return children;
}

function ClientRoutes() {
  const { user } = useAuth();
  return (
    <Routes>
      <Route path="/login" element={user ? <Navigate to="/" replace /> : <Login />} />
      <Route path="/accept-invite" element={<AcceptInvite />} />
      <Route element={<Protected><AppLayout /></Protected>}>
        <Route path="/" element={<Dashboard />} />
        <Route path="/analytics" element={<Analytics />} />
        <Route path="/campaigns" element={<Campaigns />} />
        <Route path="/leads" element={<Leads />} />
        <Route path="/logs" element={<CallLogs />} />
        <Route path="/inbound" element={<Inbound />} />
        <Route path="/agent" element={<AIAgent />} />
        <Route path="/integrations" element={<Integrations />} />
        <Route path="/billing" element={<Billing />} />
        <Route path="/support" element={<Support />} />
        <Route path="/whats-new" element={<WhatsNew />} />
        <Route path="/workflows" element={<Workflows />} />
        <Route path="/settings" element={<Settings />} />
      </Route>
      <Route path="/workflows/:id" element={<Protected><WorkflowBuilder /></Protected>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function AdminRoutes() {
  const { admin } = useAdminAuth();
  return (
    <Routes>
      <Route path="/admin/login" element={admin ? <Navigate to="/admin" replace /> : <AdminLogin />} />
      <Route element={<AdminProtected><AdminLayout /></AdminProtected>}>
        <Route path="/admin" element={<AdminOverview />} />
        <Route path="/admin/clients" element={<AdminClients />} />
        <Route path="/admin/clients/:id" element={<AdminClientDetail />} />
        <Route path="/admin/system" element={<AdminSystem />} />
        <Route path="/admin/security" element={<AdminSecurity />} />
        <Route path="/admin/analytics" element={<AdminAnalytics />} />
        <Route path="/admin/billing" element={<AdminBilling />} />
        <Route path="/admin/telephony" element={<AdminTelephony />} />
        <Route path="/admin/ai" element={<AdminAI />} />
        <Route path="/admin/announcements" element={<AdminAnnouncements />} />
        <Route path="/admin/tickets" element={<AdminTickets />} />
        <Route path="/admin/settings" element={<AdminSettings />} />
        <Route path="/admin/updates" element={<AdminUpdates />} />
      </Route>
      <Route path="*" element={<Navigate to="/admin" replace />} />
    </Routes>
  );
}

function Root() {
  const isAdmin = window.location.pathname.startsWith("/admin");
  if (isAdmin) {
    return <AdminAuthProvider><AdminRoutes /></AdminAuthProvider>;
  }
  return <AuthProvider><ClientRoutes /></AuthProvider>;
}

export default function App() {
  return (
    <BrowserRouter>
      <Root />
      <Toaster position="top-right" richColors />
    </BrowserRouter>
  );
}
